/**
 * ClientCard — Compact card for coach client list.
 */

import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { ChevronRight } from 'lucide-react-native';

const FRESHNESS_COLORS: Record<string, string> = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  gray: '#6b7280',
};

export interface ClientCardData {
  id: string;
  name: string;
  handle: string;
  avatar_url?: string | null;
  strength_score: number;
  freshness_band: string;
  last_active: string;
  baseline_pct: number; // 0-100
}

interface Props {
  client: ClientCardData;
  onPress: () => void;
}

export default function ClientCard({ client, onPress }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const initial = (client.name || client.handle || '?')[0].toUpperCase();
  const baselineCount = Math.round((client.baseline_pct / 100) * 5);
  const dotColor = FRESHNESS_COLORS[client.freshness_band] || FRESHNESS_COLORS.gray;

  const lastActiveLabel = (() => {
    if (!client.last_active) return 'Never';
    const diff = Date.now() - new Date(client.last_active).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  })();

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
      activeOpacity={0.7}
      onPress={onPress}
    >
      <View style={[styles.avatar, { backgroundColor: c.accent + '22' }]}>
        <Text style={[styles.avatarText, { color: c.accent }]}>{initial}</Text>
      </View>

      <View style={styles.info}>
        <View style={styles.nameRow}>
          <Text style={[styles.name, { color: c.text }]} numberOfLines={1}>
            {client.name || client.handle}
          </Text>
          <View style={[styles.freshnessDot, { backgroundColor: dotColor }]} />
        </View>
        <Text style={[styles.handle, { color: c.textSecondary }]} numberOfLines={1}>
          @{client.handle}
        </Text>
        <View style={styles.statsRow}>
          <Text style={[styles.statText, { color: c.textSecondary }]}>
            Baseline {baselineCount}/5
          </Text>
          <Text style={[styles.statText, { color: c.textSecondary }]}>
            STR {client.strength_score}
          </Text>
          <Text style={[styles.statText, { color: c.textSecondary }]}>
            {lastActiveLabel}
          </Text>
        </View>
      </View>

      <ChevronRight size={18} color={c.textTertiary} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    gap: 12,
    marginBottom: 8,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '800',
  },
  info: {
    flex: 1,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  name: {
    fontSize: 15,
    fontWeight: '700',
  },
  freshnessDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  handle: {
    fontSize: 12,
    fontWeight: '500',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 2,
  },
  statText: {
    fontSize: 11,
    fontWeight: '600',
  },
});
